import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Box, Typography, Paper, Table, TableHead, TableRow, 
  TableCell, TableBody, Button, Chip, Divider, TextField,
  CircularProgress, Accordion, AccordionSummary, AccordionDetails,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert,
  Menu, MenuItem
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ArrowBack, CheckCircle, Warning, HourglassEmpty, Cancel, LocalShipping, Edit, PlayArrow, DoneAll, Image, ContentCopy, FileDownloadOutlined, TableChartOutlined } from '@mui/icons-material';
import { api } from '../../services/api';
import { OrderDirection, OrderStatus } from '../../types/api';
import { generateOrderImages } from '../../utils/exportOrderImage';
import * as XLSX from 'xlsx';

interface OrderItem {
  id: number;
  item_id: number;
  item_name: string;
  category: string;
  unit: string;
  order_qty: number;
  pending_qty: number;
  shipment_created_qty?: number; // Quantity already shipped/packed
}

interface PlannedShipmentItem {
  item_id: number;
  item_name?: string;
  quantity: number;
}

interface PlannedShipment {
  order_id: number;
  items: PlannedShipmentItem[];
}

interface ItemDetail {
  id: number;
  name: string;
  category: string;
  unit: string;
}

interface OrderMetadata {
  note?: string;
  transport_provider?: string;
  planned_previous_orders_shipment?: PlannedShipment[];
}

interface OrderDetail {
  id: number;
  direction: string;
  partner_id: number;
  partner_name: string;
  status: string;
  metadata?: OrderMetadata;
  created_by: number;
  creator_name: string;
  items: OrderItem[];
  created_at: string;
  updated_at: string;
}

const CustomerOrderView: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Shipment creation state
  const [shipmentQtys, setShipmentQtys] = useState<Record<number, number>>({});
  const [creatingShipment, setCreatingShipment] = useState(false);
  const [confirmingOrder, setConfirmingOrder] = useState(false);
  const [cancellingOrder, setCancellingOrder] = useState(false);
  
  // Stock data for items with remaining qty to ship
  const [itemStocks, setItemStocks] = useState<Record<number, number>>({});
  
  // Item details for planned previous orders shipment
  const [plannedItemsDetails, setPlannedItemsDetails] = useState<Record<number, ItemDetail>>({});
  
  // Force complete state
  const [forceCompleteDialogOpen, setForceCompleteDialogOpen] = useState(false);
  const [forceCompleteReason, setForceCompleteReason] = useState('');
  const [forcingComplete, setForcingComplete] = useState(false);
  const [forceCompleteError, setForceCompleteError] = useState<string | null>(null);

  // Export as image state
  const [exportingImage, setExportingImage] = useState(false);
  const [exportedImages, setExportedImages] = useState<{ blob: Blob; url: string }[]>([]);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [copyErrors, setCopyErrors] = useState<Record<number, string>>({});

  // Export menu
  const [exportAnchorEl, setExportAnchorEl] = useState<null | HTMLElement>(null);

  useEffect(() => {
    let isCancelled = false;
    
    const fetchOrder = () => {
      api.get(`/api/orders/${id}`)
        .then(res => {
          if (isCancelled) return;
          setOrder(res.data);
          // Initialize shipment quantities to 0
          const initialQtys: Record<number, number> = {};
          res.data.items?.forEach((item: OrderItem) => {
            initialQtys[item.id] = 0;
          });
          setShipmentQtys(initialQtys);
          setLoading(false);
        })
        .catch(err => {
          if (isCancelled) return;
          console.error(err);
          setLoading(false);
        });
    };
    
    fetchOrder();
    
    return () => {
      isCancelled = true;
    };
  }, [id]);

  // Revoke exported image object URLs on unmount to avoid memory leaks
  useEffect(() => {
    return () => {
      exportedImages.forEach(image => URL.revokeObjectURL(image.url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportedImages]);

  const refetchOrder = () => {
    api.get(`/api/orders/${id}`)
      .then(res => {
        setOrder(res.data);
        // Initialize shipment quantities to 0
        const initialQtys: Record<number, number> = {};
        res.data.items?.forEach((item: OrderItem) => {
          initialQtys[item.id] = 0;
        });
        setShipmentQtys(initialQtys);
      })
      .catch(err => {
        console.error(err);
      });

  };

  const handleShipmentQtyChange = (itemId: number, value: string, maxQty: number, itemIdForStock: number) => {
    const numValue = value === '' ? 0 : parseInt(value);
    if (isNaN(numValue) || numValue < 0) return;
    // Cap at minimum of remainingToShip and available stock
    const availableStock = itemStocks[itemIdForStock] ?? 0;
    const cappedValue = Math.min(numValue, maxQty, availableStock);
    setShipmentQtys(prev => ({ ...prev, [itemId]: cappedValue }));
  };

  const handleCreateShipment = async () => {
    if (!order) return;
    
    // Filter items with qty > 0
    const itemsToShip = order.items
      .filter(item => shipmentQtys[item.id] > 0)
      .map(item => ({
        item_id: item.item_id,
        quantity: shipmentQtys[item.id],
      }));

    if (itemsToShip.length === 0) {
      return;
    }

    setCreatingShipment(true);
    try {
      await api.post('/api/shipments', {
        order_id: order.id,
        items: itemsToShip,
      });
      // Refresh order data
      refetchOrder();
    } catch (err) {
      console.error('Error creating shipment:', err);
    } finally {
      setCreatingShipment(false);
    }
  };

  const handleConfirmOrder = async () => {
    if (!order) return;
    
    setConfirmingOrder(true);
    try {
      await api.post(`/api/orders/${order.id}/confirm`);
      // Refresh order data
      refetchOrder();
    } catch (err) {
      console.error('Error confirming order:', err);
    } finally {
      setConfirmingOrder(false);
    }
  };

  const handleCancelOrder = async () => {
    if (!order) return;
    
    if (!window.confirm('Are you sure you want to cancel this order?')) {
      return;
    }
    
    setCancellingOrder(true);
    try {
      await api.post(`/api/orders/${order.id}/cancel`);
      // Refresh order data
      refetchOrder();
    } catch (err) {
      console.error('Error cancelling order:', err);
    } finally {
      setCancellingOrder(false);
    }
  };

  const handleForceComplete = async () => {
    if (!order || !forceCompleteReason.trim()) return;
    
    setForcingComplete(true);
    setForceCompleteError(null);
    try {
      await api.post(`/api/orders/${order.id}/force-complete`, {
        reason: forceCompleteReason.trim()
      });
      setForceCompleteDialogOpen(false);
      setForceCompleteReason('');
      // Refresh order data
      refetchOrder();
    } catch (err: any) {
      console.error('Error force completing order:', err);
      setForceCompleteError(err.message|| 'Failed to force complete order');
    } finally {
      setForcingComplete(false);
    }
  };

  // Merge order items with items from linked previous-order shipments (summing
  // quantities for the same item_id), dropping anything that nets to zero.
  // Order is preserved: order.items first (as shown in the main table), then
  // any additional items from planned shipments in first-appearance order.
  const buildExportItems = (): { item_name: string; order_qty: number }[] => {
    if (!order) return [];

    const merged = new Map<number, { item_name: string; quantity: number }>();

    order.items.forEach(item => {
      const existing = merged.get(item.item_id);
      merged.set(item.item_id, {
        item_name: item.item_name,
        quantity: (existing?.quantity || 0) + item.order_qty,
      });
    });

    order.metadata?.planned_previous_orders_shipment?.forEach(shipment => {
      shipment.items.forEach(planned => {
        const existing = merged.get(planned.item_id);
        const itemName = existing?.item_name
          || plannedItemsDetails[planned.item_id]?.name
          || `Item #${planned.item_id}`;
        merged.set(planned.item_id, {
          item_name: itemName,
          quantity: (existing?.quantity || 0) + planned.quantity,
        });
      });
    });

    return Array.from(merged.values())
      .filter(entry => entry.quantity > 0)
      .map(entry => ({ item_name: entry.item_name, order_qty: entry.quantity }));
  };

  const handleExportImage = async () => {
    if (!order) return;

    setExportingImage(true);
    try {
      const blobs = await generateOrderImages({
        id: order.id,
        partner_name: order.partner_name,
        created_at: order.created_at,
        transport_provider: order.metadata?.transport_provider,
        items: buildExportItems(),
      });
      const images = blobs.map(blob => ({ blob, url: URL.createObjectURL(blob) }));
      setExportedImages(images);
      setCopyErrors({});
      setExportDialogOpen(true);
    } catch (err) {
      console.error('Error generating order image:', err);
    } finally {
      setExportingImage(false);
    }
  };

  const handleCopyImage = async (index: number) => {
    const image = exportedImages[index];
    if (!image) return;

    try {
      await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': image.blob }),
      ]);
      setCopyErrors(prev => {
        const next = { ...prev };
        delete next[index];
        return next;
      });
    } catch (err) {
      console.error('Error copying image to clipboard:', err);
      setCopyErrors(prev => ({
        ...prev,
        [index]: 'Copy failed — your browser may not support image clipboard access.',
      }));
    }
  };

  const handleCloseExportDialog = () => {
    exportedImages.forEach(image => URL.revokeObjectURL(image.url));
    setExportedImages([]);
    setCopyErrors({});
    setExportDialogOpen(false);
  };

  const handleExportExcel = () => {
    if (!order) return;

    const exportItems = buildExportItems();

    const rows: (string | number)[][] = [
      ['ORDER DETAILS'],
      ['Order ID', `#${order.id}`],
      ['Party Name', order.partner_name || 'N/A'],
      ['Status', order.status],
      ['Created At', new Date(order.created_at).toLocaleString()],
    ];

    if (order.metadata?.note) {
      rows.push(['Note', order.metadata.note]);
    }
    if (order.metadata?.transport_provider) {
      rows.push(['Transport', order.metadata.transport_provider]);
    }

    rows.push([]);
    rows.push(['#', 'Item Name', 'Qty']);

    exportItems.forEach((item, index) => {
      rows.push([index + 1, item.item_name, item.order_qty]);
    });

    const worksheet = XLSX.utils.aoa_to_sheet(rows);
    worksheet['!cols'] = [
      { wch: 15 },
      { wch: 35 },
      { wch: 10 },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Order Items');

    XLSX.writeFile(workbook, `order-${order.id}.xlsx`);
    setExportAnchorEl(null);
  };

  // Check if order has no active shipments (only for OUTWARD orders before confirmation)
  const hasNoActiveShipments = order?.items?.every(item => {
    const shippedQty = item.shipment_created_qty || 0;
    return shippedQty === 0;
  }) ?? false;

  // Check if order can be edited or confirmed (OUTWARD, CREATED, no shipments yet)
  const canEditOrConfirm = order?.direction === OrderDirection.OUTWARD &&
                           order?.status === OrderStatus.CREATED &&
                           hasNoActiveShipments;

  // Check if order can be cancelled (CREATED status or no active shipments, and not already cancelled/completed)
  const canCancelOrder = order?.status !== OrderStatus.COMPLETED &&
                         order?.status !== OrderStatus.CANCELLED &&
                         (order?.status === OrderStatus.CREATED || hasNoActiveShipments);

  // Can force complete if: not COMPLETED, not CANCELLED, and not CREATED (draft)
  const canForceComplete = order?.status !== OrderStatus.COMPLETED &&
                           order?.status !== OrderStatus.CANCELLED &&
                           order?.status !== OrderStatus.CREATED;

  const hasAnyRemainingToShip = order?.items?.some(item => {
    const shippedQty = item.shipment_created_qty || 0;
    const remainingToShip = item.order_qty + item.pending_qty - shippedQty;
    return remainingToShip > 0;
  }) ?? false;
  const hasShipmentItems = Object.values(shipmentQtys).some(qty => qty > 0);
  
  // Check if order can have new shipments created (only after confirmation, i.e., not in CREATED status)
  const canCreateShipment = order?.status !== OrderStatus.COMPLETED && 
                            order?.status !== OrderStatus.CANCELLED &&
                            order?.status !== OrderStatus.CREATED &&
                            order?.direction === OrderDirection.OUTWARD &&
                            hasAnyRemainingToShip;

  // Fetch stock for items with remaining qty to ship
  useEffect(() => {
    if (!order || order.direction !== OrderDirection.OUTWARD) return;
    
    // Skip fetching stock for orders in terminal/draft states
    if (order.status === OrderStatus.CREATED || 
        order.status === OrderStatus.CANCELLED || 
        order.status === OrderStatus.COMPLETED) return;
    
    // Get item IDs that have remaining quantity to ship
    const itemIdsToFetch = order.items
      ?.filter(item => {
        const shippedQty = item.shipment_created_qty || 0;
        const remainingToShip = item.order_qty + item.pending_qty - shippedQty;
        return remainingToShip > 0;
      })
      .map(item => item.item_id) ?? [];
    
    if (itemIdsToFetch.length === 0) return;
    
    // Fetch stock for these items (comma-separated item IDs)
    const itemIdsParam = itemIdsToFetch.join(',');
    api.get(`/api/items/stock/${itemIdsParam}`)
      .then(res => {
        const data = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.stocks)
          ? res.data.stocks
          : Array.isArray(res.data?.data)
          ? res.data.data
          : [];
        
        // Map item_id to quantity
        const stockMap: Record<number, number> = {};
        data.forEach((stock: { id?: number; item_id?: number; available_quantity?: number; quantity?: number }) => {
          const itemId = stock.item_id ?? stock.id;
          if (itemId !== undefined) {
            stockMap[itemId] = stock.available_quantity ?? stock.quantity ?? 0;
          }
        });
        setItemStocks(stockMap);
      })
      .catch(err => {
        console.error('Error fetching stock:', err);
      });
  }, [order]);

  // Fetch item details for planned previous orders shipment
  useEffect(() => {
    if (!order?.metadata?.planned_previous_orders_shipment?.length) return;
    
    // Collect all unique item IDs from planned shipments
    const itemIds = new Set<number>();
    order.metadata.planned_previous_orders_shipment.forEach(shipment => {
      shipment.items.forEach(item => {
        itemIds.add(item.item_id);
      });
    });
    
    if (itemIds.size === 0) return;
    
    // Fetch item details
    const itemIdsParam = Array.from(itemIds).join(',');
    api.get(`/api/items?ids=${itemIdsParam}`)
      .then(res => {
        const items = res.data?.items || [];
        const detailsMap: Record<number, ItemDetail> = {};
        items.forEach((item: ItemDetail) => {
          detailsMap[item.id] = item;
        });
        setPlannedItemsDetails(detailsMap);
      })
      .catch(err => {
        console.error('Error fetching planned items details:', err);
      });
  }, [order?.metadata?.planned_previous_orders_shipment]);

  const getStatusChip = (status: string) => {
    switch (status) {
      case OrderStatus.CREATED:
        return <Chip icon={<Edit />} label="Draft" color="info" />;
      case OrderStatus.COMPLETED:
        return <Chip icon={<CheckCircle />} label="Completed" color="success" />;
      case OrderStatus.PARTIAL_FULFILLED:
        return <Chip icon={<Warning />} label="Partial Fulfilled" color="warning" />;
      case OrderStatus.PENDING:
        return <Chip icon={<HourglassEmpty />} label="Pending" color="primary" />;
      case OrderStatus.CANCELLED:
        return <Chip icon={<Cancel />} label="Cancelled" color="error" />;
      default:
        return <Chip label={status} />;
    }
  };

  if (loading) {
    return <Typography sx={{ p: 3 }}>Loading...</Typography>;
  }

  if (!order) {
    return <Typography sx={{ p: 3 }}>Order not found</Typography>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>
        Back to Orders
      </Button>

      <Paper sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            Order #{order.id}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={exportingImage ? <CircularProgress size={20} /> : <Image />}
              onClick={handleExportImage}
              disabled={exportingImage}
            >
              {exportingImage ? 'Generating...' : 'Get Order Receipt'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<FileDownloadOutlined />}
              onClick={(e) => setExportAnchorEl(e.currentTarget)}
              size="small"
            >
              Export
            </Button>
            <Menu
              anchorEl={exportAnchorEl}
              open={Boolean(exportAnchorEl)}
              onClose={() => setExportAnchorEl(null)}
            >
              <MenuItem onClick={handleExportExcel}>
                <TableChartOutlined sx={{ mr: 1 }} />
                Export as Excel
              </MenuItem>
            </Menu>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Order Info */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">Party Name</Typography>
            <Typography variant="h6">{order.partner_name || 'N/A'}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Status</Typography>
            <Box sx={{ mt: 0.5 }}>{getStatusChip(order.status)}</Box>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Direction</Typography>
            <Typography variant="body1">{order.direction}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Created At</Typography>
            <Typography variant="body1">{new Date(order.created_at).toLocaleString()}</Typography>
          </Box>
        </Box>

        {order.metadata?.note && (
          <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">Remark / Note</Typography>
            <Typography variant="body1" sx={{ fontStyle: 'italic' }}>{order.metadata.note}</Typography>
          </Box>
        )}

        {order.metadata?.transport_provider && (
          <Box sx={{ mb: 3, p: 2, bgcolor: '#e3f2fd', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">Transport Preference</Typography>
            <Typography variant="body1" fontWeight="medium">{order.metadata.transport_provider}</Typography>
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Items Table */}
        <Typography variant="h6" gutterBottom>Order Items</Typography>
        <Table>
          <TableHead sx={{ bgcolor: '#f5f5f5' }}>
            <TableRow>
              <TableCell>Item Name</TableCell>
              <TableCell>Category</TableCell>
              <TableCell align="center">Order Qty</TableCell>
              {order.direction === OrderDirection.OUTWARD && (
                <TableCell align="center">Pending Qty</TableCell>
              )}
              {order.direction === OrderDirection.OUTWARD && order.status !== OrderStatus.CREATED && (
                <TableCell align="center">In Process / Processed</TableCell>
              )}
              {order.direction === OrderDirection.OUTWARD && canCreateShipment && (
                <>
                  <TableCell align="center">Stock</TableCell>
                  <TableCell align="center">Ship Qty</TableCell>
                </>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {order.items && order.items.map((item) => {
              const shippedQty = item.shipment_created_qty || 0;
              const remainingToShip = item.order_qty + item.pending_qty - shippedQty;
              return (
                <TableRow key={item.id}>
                  <TableCell>{item.item_name}</TableCell>
                  <TableCell>{item.category}</TableCell>
                  <TableCell align="center">{item.order_qty}</TableCell>
                  {order.direction === OrderDirection.OUTWARD && (
                    <TableCell align="center">{item.pending_qty}</TableCell>
                  )}
                  {order.direction === OrderDirection.OUTWARD && order.status !== OrderStatus.CREATED && (
                    <TableCell align="center">
                      <Chip
                        label={shippedQty}
                        size="small"
                        color={shippedQty > 0 ? 'info' : 'default'}
                        variant={shippedQty > 0 ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                  )}
                  {order.direction === OrderDirection.OUTWARD && canCreateShipment && (
                    <>
                      <TableCell align="center">
                        <Chip
                          label={itemStocks[item.item_id] ?? '-'}
                          size="small"
                          color={
                            itemStocks[item.item_id] === undefined
                              ? 'default'
                              : itemStocks[item.item_id] <= 0
                              ? 'error'
                              : itemStocks[item.item_id] < remainingToShip
                              ? 'warning'
                              : 'success'
                          }
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <TextField
                          size="small"
                          type="number"
                          value={shipmentQtys[item.id] || 0}
                          onChange={(e) => handleShipmentQtyChange(item.id, e.target.value, remainingToShip, item.item_id)}
                          disabled={remainingToShip <= 0 || (itemStocks[item.item_id] ?? 0) <= 0}
                          inputProps={{ min: 0, max: Math.min(remainingToShip, itemStocks[item.item_id] ?? 0), style: { textAlign: 'center' } }}
                          sx={{ width: 80 }}
                        />
                      </TableCell>
                    </>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        <Divider sx={{ my: 2 }} />
        {/* Linked Previous Order Fulfilment */}
        {order.metadata?.planned_previous_orders_shipment && order.metadata.planned_previous_orders_shipment.length > 0 && (
          <Accordion sx={{ mb: 3 }} defaultExpanded={false}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography fontWeight="medium">
                Linked Previous Order Fulfilment ({order.metadata.planned_previous_orders_shipment.length})
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {order.metadata.planned_previous_orders_shipment.map((shipment, index) => (
                <Box key={shipment.order_id} sx={{ mb: index < order.metadata!.planned_previous_orders_shipment!.length - 1 ? 2 : 0 }}>
                  <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>
                    Order #{shipment.order_id}
                  </Typography>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: '#f9f9f9' }}>
                      <TableRow>
                        <TableCell>Item Name</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell align="center">Quantity</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {shipment.items.map((item) => {
                        const itemDetail = plannedItemsDetails[item.item_id];
                        return (
                          <TableRow key={item.item_id}>
                            <TableCell>{itemDetail?.name || `Item #${item.item_id}`}</TableCell>
                            <TableCell>{itemDetail?.category || '-'}</TableCell>
                            <TableCell align="center">{item.quantity}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Box>
              ))}
            </AccordionDetails>
          </Accordion>
        )}

        {/* Action Buttons */}
        {(canEditOrConfirm || (canCancelOrder && !canEditOrConfirm) || canCreateShipment || canForceComplete) && (
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2, flexWrap: 'wrap' }}>
            {/* Cancel Order Button */}
            {canCancelOrder && (
              <Button
                variant="outlined"
                color="error"
                startIcon={cancellingOrder ? <CircularProgress size={20} color="inherit" /> : <Cancel />}
                onClick={handleCancelOrder}
                disabled={cancellingOrder || confirmingOrder}
              >
                {cancellingOrder ? 'Cancelling...' : 'Cancel Order'}
              </Button>
            )}

            {/* Edit Order Button (for OUTWARD orders before shipment creation) */}
            {canEditOrConfirm && (
              <Button
                variant="outlined"
                color="primary"
                startIcon={<Edit />}
                onClick={() => navigate(`/customers/orders/outbound/${order.id}/edit`)}
              >
                Edit Order
              </Button>
            )}

            {/* Confirm Order Button (for OUTWARD orders before shipment creation) */}
            {canEditOrConfirm && (
              <Button
                variant="contained"
                color="success"
                startIcon={confirmingOrder ? <CircularProgress size={20} color="inherit" /> : <PlayArrow />}
                onClick={handleConfirmOrder}
                disabled={confirmingOrder || cancellingOrder}
              >
                {confirmingOrder ? 'Confirming...' : 'Confirm Order'}
              </Button>
            )}

            {/* Create Shipment Button */}
            {canCreateShipment && (
              <Button
                variant="contained"
                color="primary"
                startIcon={creatingShipment ? <CircularProgress size={20} color="inherit" /> : <LocalShipping />}
                onClick={handleCreateShipment}
                disabled={!hasShipmentItems || creatingShipment}
              >
                {creatingShipment ? 'Creating...' : 'Create Shipment'}
              </Button>
            )}

            {/* Force Complete Button (Admin only - when all shipments are in terminal states) */}
            {canForceComplete && (
              <Button
                variant="contained"
                color="warning"
                startIcon={<DoneAll />}
                onClick={() => setForceCompleteDialogOpen(true)}
              >
                Mark as Completed
              </Button>
            )}
          </Box>
        )}

        {/* Force Complete Dialog */}
        <Dialog 
          open={forceCompleteDialogOpen} 
          onClose={() => {
            if (!forcingComplete) {
              setForceCompleteDialogOpen(false);
              setForceCompleteError(null);
            }
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Force Complete Order</DialogTitle>
          <DialogContent>
            {forceCompleteError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setForceCompleteError(null)}>
                {forceCompleteError}
              </Alert>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Are you sure you want to mark this order as completed? This action cannot be undone.
              Please provide a reason for force completion.
            </Typography>
            <TextField
              autoFocus
              fullWidth
              label="Reason *"
              multiline
              rows={3}
              value={forceCompleteReason}
              onChange={(e) => setForceCompleteReason(e.target.value)}
              placeholder="Enter reason for force completion..."
              disabled={forcingComplete}
            />
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setForceCompleteDialogOpen(false)} 
              disabled={forcingComplete}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleForceComplete}
              variant="contained"
              color="warning"
              disabled={!forceCompleteReason.trim() || forcingComplete}
              startIcon={forcingComplete ? <CircularProgress size={20} color="inherit" /> : <DoneAll />}
            >
              {forcingComplete ? 'Completing...' : 'Mark as Completed'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Order Receipt Dialog */}
        <Dialog open={exportDialogOpen} onClose={handleCloseExportDialog} maxWidth="xs" fullWidth>
          <DialogTitle>Order Receipt</DialogTitle>
          <DialogContent>
            {exportedImages.map((image, index) => (
              <Box key={index} sx={{ mb: index < exportedImages.length - 1 ? 3 : 0 }}>
                {exportedImages.length > 1 && (
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Page {index + 1} of {exportedImages.length}
                  </Typography>
                )}
                <Box
                  component="img"
                  src={image.url}
                  alt={`Order image page ${index + 1}`}
                  sx={{ width: '100%', border: '1px solid #e0e0e0', borderRadius: 1, display: 'block' }}
                />
                {copyErrors[index] && (
                  <Alert severity="error" sx={{ mt: 1 }}>{copyErrors[index]}</Alert>
                )}
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<ContentCopy />}
                  onClick={() => handleCopyImage(index)}
                  sx={{ mt: 1 }}
                >
                  {exportedImages.length > 1 ? `Copy Page ${index + 1}` : 'Copy Image'}
                </Button>
              </Box>
            ))}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseExportDialog}>Close</Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Box>
  );
};

export default CustomerOrderView;
